import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { devError } from "@/lib/logger";

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem("tx_company_id") || "");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      setActiveId((prev) => {
        if (prev) return prev;
        if (data.length) {
          localStorage.setItem("tx_company_id", data[0].id);
          return data[0].id;
        }
        return prev;
      });
    } catch (err) {
      devError("Failed to load companies", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refresh();
    } else {
      setCompanies([]);
      setActiveId("");
    }
  }, [user, refresh]);

  const setActive = useCallback((id) => {
    setActiveId(id);
    localStorage.setItem("tx_company_id", id);
  }, []);

  const active = useMemo(
    () => companies.find((c) => c.id === activeId) || null,
    [companies, activeId]
  );

  const value = useMemo(
    () => ({ companies, active, activeId, setActive, refresh, loading }),
    [companies, active, activeId, setActive, refresh, loading]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export const useCompany = () => useContext(CompanyContext);
